import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import MarketingLayout from '../layouts/MarketingLayout';
import { ThemeContext } from '../context/ThemeContext';
import { gsap } from 'gsap';
import Ripple from '../components/Ripple';
import Spotlight from '../components/Spotlight';
import SpotlightCard from '../components/SpotlightCard';
import CardSwap, { Card } from '../components/CardSwap';
import ProductPreview from '../components/marketing/ProductPreview';
import {
  FiFileText,
  FiUsers,
  FiTrendingUp,
  FiBarChart,
  FiCheckCircle,
  FiTarget,
  FiPieChart,
  FiArrowRight,
  FiPlay,
  FiDownload,
  FiCamera,
  FiDollarSign,
  FiActivity,
  FiZap,
} from 'react-icons/fi';

const Funcionalidades = () => {
  const navigate = useNavigate();
  const { modoOscuro } = useContext(ThemeContext);
  const [activeSection, setActiveSection] = useState(0);
  const section1Ref = useRef(null);
  const section2Ref = useRef(null);
  const section3Ref = useRef(null);
  const section4Ref = useRef(null);

  const scrollDuration = 1.2;
  const scrollEase = 'power2.inOut';
  const isScrollingRef = useRef(false);
  const activeSectionRef = useRef(0);

  useEffect(() => {
    activeSectionRef.current = 0;
    window.__funcionalidadesActiveSection = 0;
    setActiveSection(0);
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      tl.fromTo('.hero-badge', { opacity: 0, y: -30 }, { opacity: 1, y: 0, duration: 0.6 });
      tl.fromTo('.hero-title', { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 0.8 }, '-=0.3');
      tl.fromTo('.hero-desc', { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.6 }, '-=0.4');
      tl.fromTo('.hero-cta', { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.6 }, '-=0.3');
      tl.fromTo('.hero-benefits > *', { opacity: 0, x: -20 }, { opacity: 1, x: 0, duration: 0.4, stagger: 0.1 }, '-=0.3');
      tl.fromTo('.hero-showcase', { opacity: 0, x: 100 }, { opacity: 1, x: 0, duration: 0.8 }, '-=0.8');
    }, section1Ref);
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const container = document.getElementById('main-scroll');
    if (!container) return;

    const isMobile = window.innerWidth < 768;
    const sections = [section1Ref, section2Ref, section3Ref, section4Ref];

    const scrollToSection = (index) => {
      isScrollingRef.current = true;

      const targetPosition = index === 0 ? 0 : (() => {
        const targetElement = sections[index].current;
        if (!targetElement) return 0;
        const containerRect = container.getBoundingClientRect();
        const targetRect = targetElement.getBoundingClientRect();
        return container.scrollTop + (targetRect.top - containerRect.top);
      })();

      gsap.to(container, {
        scrollTop: targetPosition,
        duration: scrollDuration,
        ease: scrollEase,
        onComplete: () => {
          isScrollingRef.current = false;
          activeSectionRef.current = index;
          window.__funcionalidadesActiveSection = index;
          setActiveSection(index);
        },
      });
    };

    const handleWheel = (e) => {
      if (isMobile || isScrollingRef.current) return;
      e.preventDefault();

      const currentSection = activeSectionRef.current;
      if (e.deltaY > 0 && currentSection < sections.length - 1) {
        scrollToSection(currentSection + 1);
      } else if (e.deltaY < 0 && currentSection > 0) {
        scrollToSection(currentSection - 1);
      }
    };

    const handleScroll = () => {
      if (isScrollingRef.current) return;
      const containerHeight = container.clientHeight;
      const scrollTop = container.scrollTop;

      sections.forEach((ref, index) => {
        if (!ref.current) return;
        if (index === 0) {
          if (scrollTop < containerHeight / 2 && activeSectionRef.current !== 0) {
            activeSectionRef.current = 0;
            window.__funcionalidadesActiveSection = 0;
            setActiveSection(0);
          }
          return;
        }
        const rect = ref.current.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const elementTop = rect.top - containerRect.top;
        if (elementTop >= -containerHeight / 2 && elementTop < containerHeight / 2) {
          if (activeSectionRef.current !== index) {
            activeSectionRef.current = index;
            window.__funcionalidadesActiveSection = index;
            setActiveSection(index);
          }
        }
      });
    };

    window.__funcionalidadesScroll = {
      goToSection: scrollToSection,
      goToHero: () => scrollToSection(0),
    };
    if (!isMobile) {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }
    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      if (!isMobile) container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('scroll', handleScroll);
      delete window.__funcionalidadesScroll;
    };
  }, []);

  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const sectionBg = modoOscuro ? 'bg-black' : 'bg-white';
  const heroText = modoOscuro ? 'text-white' : 'text-gray-900';
  const heroTextMuted = modoOscuro ? 'text-gray-300' : 'text-gray-600';
  const iconBox = modoOscuro ? 'bg-[#004AAD]/30 text-[#5CA3FF]' : 'bg-[#004AAD]/10 text-[#004AAD]';
  const heroBtnPrimary = modoOscuro
    ? 'bg-white text-[#004aad] border-white hover:bg-[#00357a] hover:text-white hover:border-[#00357a]'
    : 'bg-black text-white border-black hover:bg-gray-800';
  const heroBtnSecondary = modoOscuro
    ? 'border-2 border-white text-white hover:bg-white hover:text-[#004aad]'
    : 'border-2 border-black text-black hover:bg-black hover:text-white';

  const heroHighlights = [
    {
      icon: FiDollarSign,
      title: 'Cobrá más',
      text: 'Quién cobrar primero hoy, recordatorios por mail con PDF y tu logo (Profesional+) y link de Mercado Pago por WhatsApp.',
      preset: 'cobrar',
    },
    {
      icon: FiDownload,
      title: 'Puente con tu contador',
      text: 'Paquete mensual descargable, pantalla de solo lectura para el estudio y varios clientes en un portal.',
      preset: 'contador',
    },
    {
      icon: FiActivity,
      title: 'Panorama de cobros',
      text: 'Cuántos días tardás en cobrar, ranking de clientes, avisos por mail y resumen en PDF.',
      preset: 'analisis',
    },
  ];

  const heroPreviewPresets = ['dashboard', ...heroHighlights.map(({ preset }) => preset)];

  const cobranzaDual = [
    {
      icon: FiDollarSign,
      title: 'Cobrar',
      subtitle: 'El día a día',
      text:
        'Quién te debe, mensajes por WhatsApp o mail, PDF de comprobante con tus datos de emisor, link de Mercado Pago por factura, promesas de pago y la lista de “hacé esto primero”.',
    },
    {
      icon: FiBarChart,
      title: 'Cobranzas en Reportes',
      subtitle: 'El panorama',
      text:
        'Cómo venís mes a mes, cuánto tardás en cobrar, descarga para Excel y qué tan rentable es cada cliente (Profesional+).',
    },
  ];

  const features = [
    {
      icon: <FiFileText className="w-4 h-4" />,
      title: 'Registro de ventas y gastos',
      description:
        'Cargá a mano, repetí el último movimiento o traé datos desde Mercado Pago, una planilla o escaneá un comprobante (Profesional+). Cada gasto puede quedar pagado o pendiente: cuenta en el mes aunque todavía no lo hayas abonado. Cruzá el extracto del banco con lo cargado (Profesional+).',
      highlights: ['Mercado Pago y planillas de gastos', 'Escaneo de comprobante → gasto (Profesional+)', 'Estado pagado o pendiente'],
    },
    {
      icon: <FiUsers className="w-4 h-4" />,
      title: 'Clientes y quién te debe',
      description:
        'Ves por cliente cuánto facturaste, cuánto entró y qué sigue pendiente. En Profesional+ un indicador te ayuda a ver quién suele pagar tarde.',
      highlights: ['Facturado, cobrado y pendiente', 'Indicador por cliente (Profesional+)'],
    },
    {
      icon: <FiDownload className="w-4 h-4" />,
      title: 'Vista contador y mes proyectado',
      description:
        'Tu contador entra a mirar sin tocar tus números (Profesional+). Cada mes podés bajar un paquete con ventas, gastos, impagas y resultado del período.',
      highlights: ['Pantalla solo lectura (Profesional+)', 'Paquete mensual automático (Profesional+)'],
    },
    {
      icon: <FiPieChart className="w-4 h-4" />,
      title: 'Ayuda con impuestos y vencimientos',
      description:
        'Calendario de Monotributo e IVA, avisos en Inicio y simulador de categoría (Profesional+). Monotributistas: la cuota mensual puede anotarse sola en egresos (mes devengado, vencimiento configurable y marcar pagada cuando transferís). Orientativo: no reemplaza al contador ni a la facturación oficial.',
      highlights: ['Vencimientos con recordatorios', 'Cuota Monotributo en egresos', 'Lista del mes (Profesional+)'],
    },
    {
      icon: <FiTrendingUp className="w-4 h-4" />,
      title: 'Reportes, resultados y equipo',
      description:
        'Plata en caja, cuánto ganaste o perdiste en el período, presupuestos y la bandeja del día. Varias empresas en el plan Empresarial.',
      highlights: ['Resultado del mes', 'Presupuestos con avisos (Empresarial)'],
    },
  ];

  const helpMoments = [
    {
      icon: <FiDollarSign className="w-5 h-5" />,
      when: 'Cuando te deben',
      title: 'Entrás a Cobrar y actuás',
      description:
        'Lista de lo impago ordenada por prioridad: WhatsApp, mail, link de Mercado Pago por factura y fecha en la que te dijeron que pagan.',
    },
    {
      icon: <FiActivity className="w-5 h-5" />,
      when: 'Cuando querés el panorama',
      title: 'Cobranzas en Reportes',
      description:
        'Últimos seis meses en gráficos, cuántos días tardás en cobrar, planilla para Excel para tu contador y cuánto te deja cada cliente (Profesional+).',
    },
    {
      icon: <FiTrendingUp className="w-5 h-5" />,
      when: 'Cuando cierra el mes',
      title: 'La bandeja te dice qué hacer hoy',
      description:
        'Impagas, clientes que vienen pagando mal, vencimientos de impuestos y pasos del cierre del mes en una sola pantalla.',
    },
    {
      icon: <FiDownload className="w-5 h-5" />,
      when: 'Cuando llama el contador',
      title: 'Vista contador y paquete del mes',
      description:
        'El estudio mira tus números sin editarlos (Profesional+) y baja un archivo con ventas, gastos, lo que falta cobrar y el resultado del período.',
    },
    {
      icon: <FiPieChart className="w-5 h-5" />,
      when: 'Cuando el régimen aprieta',
      title: 'Simulador y calendario fiscal',
      description:
        'Fechas importantes, tope de Monotributo, cuota del mes en egresos con estado pendiente o pagada y simulador de categoría. Es una guía: la palabra final la tiene tu contador.',
    },
    {
      icon: <FiCamera className="w-5 h-5" />,
      when: 'Cuando traés movimientos de afuera',
      title: 'Mercado Pago, planilla o escaneo',
      description:
        'Cobros de Mercado Pago como ventas, gastos desde planilla columna por columna, o escaneo del ticket que prellena monto y fecha para que confirmes (Profesional+).',
    },
    {
      icon: <FiFileText className="w-5 h-5" />,
      when: 'Cuando mandás recordatorios',
      title: 'PDF con tu marca',
      description:
        'En Cuenta cargás logo, nombre, domicilio (país y provincia) y teléfono internacional: el PDF que adjuntás al cobrar muestra tu emisor, no una plantilla genérica (Profesional+).',
    },
  ];

  const ctaReassurance = [
    '30 días para probar sin presión',
    'Desde anotar un gasto hasta mandar el material al contador',
    'Pensado para negocios chicos en Argentina',
  ];

  const workflow = [
    {
      step: '01',
      icon: <FiFileText className="w-5 h-5" />,
      title: 'Cargá',
      description: 'Una venta, un gasto o un cliente. Duplicá el último, escaneá un comprobante o activá la cuota de Monotributo en Fiscal para que se anote sola.',
    },
    {
      step: '02',
      icon: <FiBarChart className="w-5 h-5" />,
      title: 'Mirá el mes',
      description: 'Bandeja del día, caja e impagas. El período entra en una pantalla.',
    },
    {
      step: '03',
      icon: <FiTarget className="w-5 h-5" />,
      title: 'Actuá',
      description:
        'Cobrás desde la bandeja, mirás gráficos en Reportes o le abrís la pantalla de solo lectura a tu contador (Profesional+).',
    },
  ];

  return (
    <MarketingLayout showNavbar={activeSection === 0}>
      <div className="bg-transparent">
        <section ref={section1Ref} className={`relative min-h-screen flex flex-col justify-center py-12 lg:py-0 overflow-hidden ${modoOscuro ? 'bg-black' : 'bg-white'}`}>
          <Ripple
            mainCircleSize={300}
            numCircles={6}
            className={modoOscuro ? 'text-[#1F80FF]/50' : 'text-[#004AAD]/25'}
          />
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 lg:pt-16">
            <div className="flex flex-col lg:grid lg:grid-cols-2 lg:items-center gap-6 lg:gap-12 xl:gap-16">
              <div className="text-center lg:text-left space-y-4 sm:space-y-5">
                <p className={`hero-badge text-[11px] font-semibold tracking-[0.18em] uppercase ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}>
                  Funcionalidades
                </p>
                <h1 className={`hero-title text-3xl sm:text-4xl md:text-5xl font-extrabold leading-[1.08] tracking-tight ${heroText}`}>
                  Cobrá mejor. Llevale orden al contador.
                </h1>
                <p className={`hero-desc text-sm sm:text-base lg:text-lg leading-relaxed max-w-lg mx-auto lg:mx-0 ${heroTextMuted}`}>
                  No somos un facturador electrónico: te ayudamos a saber qué te deben, a cobrar con orden y a cerrar el mes con tu contador. Registrá ventas y gastos, seguí quién paga tarde, mandá recordatorios y llevale un paquete claro al estudio — sin hablar en códigos raros.
                </p>
                <div className="hero-cta flex flex-col sm:flex-row gap-2.5 justify-center lg:justify-start pt-1">
                  <button
                    type="button"
                    onClick={() => navigate('/register')}
                    className={`group px-5 py-3 rounded-xl font-semibold text-sm border transition-all duration-300 shadow-lg hover:shadow-xl active:scale-95 ${heroBtnPrimary}`}
                  >
                    Empezar gratis
                    <FiArrowRight className="inline-block ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/planes')}
                    className={`group px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-300 active:scale-95 ${heroBtnSecondary}`}
                  >
                    <FiPlay className="inline-block mr-2 w-4 h-4" />
                    Ver planes
                  </button>
                </div>
                <div className="hero-benefits grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
                  {heroHighlights.map(({ icon: Icon, title, text }) => (
                    <SpotlightCard
                      key={title}
                      className={`rounded-xl p-3 border shadow-sm text-left ${modoOscuro ? 'bg-gray-900/70 border-gray-700' : 'bg-white/80 border-gray-200'}`}
                    >
                      <div className={`w-8 h-8 rounded-lg mb-2 flex items-center justify-center ${iconBox}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <p className={`text-xs font-semibold ${heroText}`}>{title}</p>
                      <p className={`text-[11px] leading-snug mt-0.5 ${heroTextMuted}`}>{text}</p>
                    </SpotlightCard>
                  ))}
                </div>
              </div>

              <div className="lg:hidden w-full max-w-md mx-auto">
                <ProductPreview variant="compact" preset="dashboard" idPrefix="func-mobile" />
              </div>

              <div className="hero-showcase hidden lg:flex items-center lg:justify-center relative w-full px-4 xl:px-8 max-w-xl lg:max-w-none">
                <CardSwap
                  containerClassName="card-swap-hero"
                  width={420}
                  height={480}
                  cardDistance={50}
                  verticalDistance={60}
                  delay={5000}
                  pauseOnHover={false}
                  skewAmount={2}
                  easing="smooth"
                >
                  {heroPreviewPresets.map((preset) => (
                    <Card
                      key={preset}
                      customClass={`product-preview-card h-full w-full border-0 shadow-none ${modoOscuro ? 'bg-gray-900 product-preview-card--dark' : 'bg-white product-preview-card--light'}`}
                    >
                      <ProductPreview
                        preset={preset}
                        variant="swap"
                        idPrefix={`func-${preset}`}
                        className="h-full w-full text-left"
                      />
                    </Card>
                  ))}
                </CardSwap>
              </div>
            </div>
          </div>
        </section>

        <section
          ref={section2Ref}
          className={`relative min-h-screen flex flex-col justify-center py-10 lg:py-12 overflow-hidden ${sectionBg}`}
        >
          <Spotlight />
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
              <div className="lg:col-span-5 text-center lg:text-left">
                <div
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-4 ${modoOscuro ? 'bg-[#004AAD]/20 text-[#5CA3FF]' : 'bg-[#004AAD]/10 text-[#004AAD]'}`}
                >
                  <FiZap className="w-3.5 h-3.5" />
                  <span>El detalle</span>
                </div>
                <h2 className={`text-3xl md:text-4xl font-bold mb-3 leading-tight ${textPri}`}>
                  Qué hace cada parte de la app
                </h2>
                <p className={`text-sm md:text-base leading-relaxed mb-6 max-w-lg mx-auto lg:mx-0 ${textSec}`}>
                  Cargar movimientos, cobrar lo impago y cerrar el mes con tu contador. Sin tecnicismos: te contamos qué hace cada pantalla.
                </p>

                <SpotlightCard className={`rounded-xl p-4 sm:p-5 space-y-3 border shadow-sm text-left ${box}`}>
                  <p className={`text-[11px] font-semibold uppercase tracking-wide ${textMuted}`}>
                    Cobranzas en dos pantallas
                  </p>
                  {cobranzaDual.map((item, index) => (
                    <React.Fragment key={item.title}>
                      {index > 0 && <div className={`h-px ${modoOscuro ? 'bg-gray-700' : 'bg-gray-200'}`} />}
                      <div>
                        <p className={`text-sm font-bold ${textPri}`}>
                          {item.title}
                          <span className={`font-normal ${textMuted}`}> · {item.subtitle}</span>
                        </p>
                        <p className={`text-sm mt-1 leading-relaxed ${textSec}`}>{item.text}</p>
                      </div>
                    </React.Fragment>
                  ))}
                </SpotlightCard>

                <p className={`text-sm mt-5 ${textSec}`}>
                  ¿Qué incluye cada plan?{' '}
                  <Link
                    to="/planes"
                    className={`font-semibold underline underline-offset-2 ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}
                  >
                    Ver planes y precios
                  </Link>
                </p>
              </div>

              <div className="lg:col-span-7 min-w-0">
                <p className={`text-[11px] font-semibold tracking-[0.16em] uppercase mb-3 text-center lg:text-left ${textMuted}`}>
                  Módulos principales
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {features.slice(0, 4).map((feature) => (
                    <SpotlightCard
                      key={feature.title}
                      className={`${box} overflow-hidden h-full rounded-xl p-4 sm:p-5 border shadow-sm`}
                    >
                      <div className={`w-9 h-9 rounded-lg mb-3 flex items-center justify-center ${iconBox}`}>
                        {React.cloneElement(feature.icon, { className: 'w-4 h-4' })}
                      </div>
                      <h3 className={`text-sm font-semibold mb-1.5 leading-snug ${textPri}`}>{feature.title}</h3>
                      <p className={`text-xs sm:text-sm leading-relaxed line-clamp-4 ${textSec}`}>{feature.description}</p>
                    </SpotlightCard>
                  ))}
                </div>

                <SpotlightCard className={`${box} overflow-hidden mt-3 rounded-xl border p-4 sm:p-5 shadow-sm flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4`}>
                  <div className={`w-9 h-9 rounded-lg shrink-0 flex items-center justify-center ${iconBox}`}>
                    {React.cloneElement(features[4].icon, { className: 'w-4 h-4' })}
                  </div>
                  <div className="min-w-0 text-left">
                    <h3 className={`text-sm font-semibold mb-1 ${textPri}`}>{features[4].title}</h3>
                    <p className={`text-xs sm:text-sm leading-relaxed line-clamp-3 sm:line-clamp-none ${textSec}`}>
                      {features[4].description}
                    </p>
                  </div>
                </SpotlightCard>

                <p className={`text-xs mt-3 text-center lg:text-left ${textMuted}`}>
                  Más abajo: ejemplos concretos de uso en el día a día.
                </p>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => window.__funcionalidadesScroll?.goToHero()}
            className={`hidden lg:flex absolute bottom-8 right-8 w-12 h-12 rounded-full backdrop-blur-sm border items-center justify-center transition-all duration-300 group z-20 ${modoOscuro ? 'bg-white/10 border-white/20 hover:bg-white/20' : 'bg-black/10 border-black/20 hover:bg-black/20'}`}
            aria-label="Volver arriba"
          >
            <FiArrowRight className={`w-5 h-5 rotate-[-90deg] group-hover:-translate-y-1 transition-transform ${modoOscuro ? 'text-white' : 'text-gray-900'}`} />
          </button>
        </section>

        <section ref={section3Ref} className={`relative min-h-screen flex flex-col justify-start lg:justify-center py-10 lg:py-14 overflow-hidden ${sectionBg}`}>
          <Spotlight />
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 xl:px-10">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-6 lg:mb-8">
                <p className={`text-[10px] font-semibold tracking-[0.18em] uppercase mb-1.5 ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}>
                  Cómo te ayuda
                </p>
                <h2 className={`text-xl md:text-2xl font-bold mb-1.5 ${textPri}`}>
                  Te ayuda en el cobro, el cierre y el contador
                </h2>
                <p className={`text-xs md:text-sm max-w-xl mx-auto leading-snug ${textSec}`}>
                  Cargás una vez — o lo repetís, o se anota solo cada mes. Después cobrás, cerrás el mes o le mandás el material al contador.
                </p>
              </div>

              <div className="space-y-4 lg:space-y-5">
                <SpotlightCard className={`${box} rounded-2xl p-4 lg:p-5 border shadow-sm`}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                    <div className="min-w-0">
                      <p className={`text-[10px] font-semibold tracking-[0.14em] uppercase mb-0.5 ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}>
                        Así de simple
                      </p>
                      <h3 className={`text-base lg:text-lg font-bold ${textPri}`}>
                        Tres pasos y el registro ya trabaja
                      </h3>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate('/register')}
                      className={`group shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${modoOscuro ? 'bg-white text-black hover:bg-gray-200' : 'bg-gray-900 text-white hover:bg-black'}`}
                    >
                      Empezar gratis
                      <FiArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </button>
                  </div>
                  <ol className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {workflow.map((item) => (
                      <li key={item.step} className={`flex gap-3 rounded-xl p-3 min-h-[88px] ${modoOscuro ? 'bg-white/[0.03]' : 'bg-black/[0.02]'}`}>
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${iconBox}`}>
                          {item.icon}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-baseline gap-1.5 mb-1">
                            <span className={`text-[10px] font-bold tabular-nums ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}>
                              {item.step}
                            </span>
                            <h4 className={`text-sm font-bold ${textPri}`}>{item.title}</h4>
                          </div>
                          <p className={`text-xs leading-relaxed ${textSec}`}>{item.description}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </SpotlightCard>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4 items-stretch">
                  {helpMoments.map((moment) => (
                    <SpotlightCard key={moment.when} className={`${box} h-full rounded-xl p-4 border shadow-sm flex flex-col min-h-[120px]`}>
                      <div className="flex items-start gap-2.5 mb-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 [&>svg]:w-4 [&>svg]:h-4 ${iconBox}`}>
                          {moment.icon}
                        </div>
                        <p className={`text-[10px] font-semibold tracking-[0.1em] uppercase leading-snug pt-0.5 ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}>
                          {moment.when}
                        </p>
                      </div>
                      <h3 className={`text-sm font-bold mb-1.5 leading-snug ${textPri}`}>
                        {moment.title}
                      </h3>
                      <p className={`text-xs leading-relaxed mt-auto ${textSec}`}>
                        {moment.description}
                      </p>
                    </SpotlightCard>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => window.__funcionalidadesScroll?.goToHero()}
            className={`hidden lg:flex absolute bottom-8 right-8 w-12 h-12 rounded-full backdrop-blur-sm border items-center justify-center transition-all duration-300 group z-20 ${modoOscuro ? 'bg-white/10 border-white/20 hover:bg-white/20' : 'bg-black/10 border-black/20 hover:bg-black/20'}`}
            aria-label="Volver arriba"
          >
            <FiArrowRight className={`w-5 h-5 rotate-[-90deg] group-hover:-translate-y-1 transition-transform ${modoOscuro ? 'text-white' : 'text-gray-900'}`} />
          </button>
        </section>

        <section
          ref={section4Ref}
          className="relative py-16 lg:py-0 lg:min-h-screen lg:flex lg:items-center lg:justify-center overflow-hidden bg-[#004AAD]"
        >
          <div className="max-w-2xl mx-auto text-center px-4 sm:px-6 lg:px-8 w-full">
            <p className="text-[11px] font-semibold tracking-[0.14em] uppercase text-white/70 mb-3">
              El siguiente paso
            </p>
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4 leading-tight">
              Ordená tu mes y probalo con calma
            </h2>
            <p className="text-sm md:text-base text-white/90 mb-6 leading-relaxed max-w-lg mx-auto">
              Ya viste qué hace cada parte. Creá tu cuenta, cargá un cliente y anotá tu primera venta o gasto: en pocos minutos vas a ver si te ordena la cabeza.
            </p>
            <ul className="space-y-2.5 mb-8 max-w-md mx-auto text-left">
              {ctaReassurance.map((line) => (
                <li key={line} className="flex items-start gap-2.5 text-sm text-white/95">
                  <FiCheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-white" aria-hidden />
                  {line}
                </li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/register"
                className="group inline-flex items-center justify-center gap-2 bg-white text-[#004aad] px-6 py-3 rounded-xl font-semibold text-sm border border-white hover:bg-[#00357a] hover:text-white transition-colors"
              >
                Crear cuenta gratis
                <FiArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link
                to="/planes"
                className="inline-flex items-center justify-center px-6 py-3 rounded-xl font-semibold text-sm border border-white/80 text-white hover:bg-white hover:text-[#004aad] transition-colors"
              >
                Ver planes y precios
              </Link>
            </div>
            <p className="mt-6 text-xs text-white/75">
              ¿Preferís hablar antes?{' '}
              <Link to="/contacto" className="font-semibold text-white underline underline-offset-2 hover:text-white/90">
                Escribinos
              </Link>
            </p>
          </div>
          <button
            type="button"
            onClick={() => window.__funcionalidadesScroll?.goToHero()}
            className="hidden lg:flex absolute bottom-8 right-8 w-12 h-12 rounded-full backdrop-blur-sm border border-white/20 bg-white/10 items-center justify-center transition-all duration-300 group z-20 hover:bg-white/20"
            aria-label="Volver arriba"
          >
            <FiArrowRight className="w-5 h-5 rotate-[-90deg] text-white group-hover:-translate-y-1 transition-transform" />
          </button>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default Funcionalidades;
